import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { formatMoneyInput } from '@/components/MoneyInput';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { BatchSearch, formatQty, invalidateStockControl, parseMinor, productTitle } from './stock-control-common';
import type { StockBatchHit } from './stock-control-types';

interface PriceLine {
  batch: StockBatchHit;
  price: string;
}

interface RepriceResult {
  changed: number;
}

const MAX_BATCHES_PER_REPRICE = 200;

function priceText(minor: number): string {
  return formatMoneyInput(minor);
}

function lineProblem(line: PriceLine): string | null {
  const minor = parseMinor(line.price);
  if (minor === null) return 'Enter a price of zero or more';
  if (minor < line.batch.purchasePriceMinor) return `Below cost (${money(line.batch.purchasePriceMinor)})`;
  return null;
}

export function RepriceDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [lines, setLines] = useState<PriceLine[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const changedLines = lines.filter((line) => parseMinor(line.price) !== line.batch.salePriceMinor);

  const reprice = useMutation({
    mutationFn: () =>
      post<RepriceResult>('/pharmacy/stock/batches/reprice', {
        lines: changedLines.map((line) => ({ stockBatchId: line.batch.id, salePriceMinor: parseMinor(line.price) })),
        reason: reason.trim(),
      }),
    onSuccess: (result) => {
      if (result.changed === 0) {
        toast.warning(tr('pharmacy', 'no_price_changed'), tr('pharmacy', 'the_new_prices_were_the_same_as_the_current_ones'));
      } else {
        toast.success(`${result.changed} price${result.changed === 1 ? '' : 's'} changed`, tr('pharmacy', 'the_new_price_applies_to_the_next_sale'));
      }
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (lines.length === 0) missing.push('at least one batch');
  else if (changedLines.length === 0) missing.push('a price that differs from the current one');
  if (lines.some((line) => lineProblem(line) !== null)) missing.push('a valid price on every line, not below cost');
  if (reason.trim().length < 3) missing.push('a reason of 3 or more characters');

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('pharmacy', 'reprice_batches')}
      description={tr('pharmacy', 'change_the_sale_price_of_batches_on_hand_the')}
      footer={
        <>
          {missing.length > 0 && (
            <span className="me-auto self-center text-xs text-muted-foreground">{tr('pharmacy', 'still_needed')} {missing.join(', ')}</span>
          )}
          <Button variant="ghost" onClick={onClose} disabled={reprice.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              reprice.mutate();
            }}
            loading={reprice.isPending}
            disabled={missing.length > 0}
          >
            {tr('pharmacy', 'change_prices')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <BatchSearch
          isPicked={(batch) => lines.some((line) => line.batch.id === batch.id)}
          onPick={(batch) =>
            setLines((current) =>
              current.length >= MAX_BATCHES_PER_REPRICE || current.some((line) => line.batch.id === batch.id)
                ? current
                : [...current, { batch, price: priceText(batch.salePriceMinor) }],
            )
          }
        />

        {lines.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            {tr('pharmacy', 'no_batches_chosen_yet_search_above_and_add_the_2')}
          </p>
        ) : (
          <div className="space-y-2">
            {lines.map((line, index) => {
              const problem = lineProblem(line);
              return (
                <div key={line.batch.id} className="grid items-center gap-2 rounded-md border p-2 sm:grid-cols-12">
                  <div className="sm:col-span-6">
                    <p className="text-sm font-medium">{productTitle(line.batch.product)}</p>
                    <p className="text-xs text-muted-foreground">
                      {tr('pharmacy', 'batch')} <span className="font-mono">{line.batch.batchNo}</span> · {line.batch.store.name} ·{' '}
                      {formatQty(line.batch.qtyOnHand)} {line.batch.product.unit} {tr('pharmacy', 'on_hand_cost')}{' '}
                      {money(line.batch.purchasePriceMinor)} {tr('pharmacy', 'now')} {money(line.batch.salePriceMinor)}
                    </p>
                  </div>
                  <div className="sm:col-span-5">
                    <Input
                      className="tabular"
                      type="text"
                      dir="ltr"
                      inputMode="decimal"
                      aria-label={tr('pharmacy', 'new_sale_price_for_x', { batchNo: line.batch.batchNo })}
                      aria-invalid={problem !== null}
                      value={line.price}
                      onChange={(event) =>
                        setLines((current) =>
                          current.map((candidate, position) =>
                            position === index ? { ...candidate, price: event.target.value } : candidate,
                          ),
                        )
                      }
                    />
                    {problem && <p className="mt-1 text-xs font-medium text-destructive">{problem}</p>}
                  </div>
                  <Button
                    className="sm:col-span-1"
                    variant="ghost"
                    size="icon"
                    aria-label={tr('pharmacy', 'remove_x', { batchNo: line.batch.batchNo })}
                    onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <Field label={tr('pharmacy', 'reason')} required hint={tr('pharmacy', 'kept_on_the_batch_history_at_least_3_characters')}>
          <Textarea rows={2} maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
